import React, { useEffect } from "react";
import { Form, Divider, Typography, Checkbox, Select, Button } from "antd";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import AgencyMultiSelectEx from "../shared/form/AgencyMultiSelectEx";
import { listAgenciesCompactAction } from "../../store/agency/agencyActions";
import { ViewDocuments } from "../shared/securityRoleTasks";
import AgencyPreferenceTip from "./preferences/AgencyPreferenceTip";
import CustomRadioGroup from "../shared/form/reactHookForm/CustomRadioGroup";
import YesterdayShortcutTip from "./preferences/YesterdayShortCutTip";
import BatchSortingTip from "./preferences/BatchSortingTip";
import { SaveOutlined } from "@ant-design/icons";
import useUserPreferences from "../../hooks/useUserPreferences";
import { Controller } from "react-hook-form";
import sortColumnOptions from "../batching/form/sortColumnOptions";
import getMenuItemVisibility from "../navigation/getMenuItemsVisibility";
import filterUserPreferencesForSave from "./preferences/filterUserPreferencesForSave";
import isPreferenceGroupVisible from "./preferences/isPreferenceGroupVisible";

const { Title, Text } = Typography;

export default function PreferencesPanel() {
  const dispatch = useDispatch();

  const actorDetails = useSelector((state) => state.actor.details.data);
  const menuVisibility = getMenuItemVisibility(actorDetails);
  const brokerId = actorDetails?.BrokerId;
  const agencies = useSelector((state) => state.agency.listCompact);

  const {
    preferences,
    setEditableUserPreference,
    saveUserPreferences,
    loading
  } = useUserPreferences([
    "DocumentSearchDefaultAgencyKeys",
    "DisplayLowInventoryAlertHomePage",
    "NotifyLowInventoryByEmail",
    "UseYesterdayInToDateShortcuts",
    "BatchSortColumn",
    "BatchSortAscending",
  ]);

  const {
    reset,
    getValues,
    control,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      DocumentSearchDefaultAgencyKeys: [],
      DisplayLowInventoryAlertHomePage: false,
      NotifyLowInventoryByEmail: false,
      UseYesterdayInToDateShortcuts: undefined,
      BatchSortColumn: undefined,
      BatchSortAscending: undefined,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (brokerId != null) {
      dispatch(listAgenciesCompactAction(brokerId, ViewDocuments));
    }
  }, [brokerId]);

  useEffect(() => {
    if (!agencies || !preferences) return;

    const agencyIds = parseAgencyPreference(
      preferences.DocumentSearchDefaultAgencyKeys,
      agencies
    );

    reset(
      {
        ...preferences,
        DocumentSearchDefaultAgencyKeys: agencyIds,
      },
      { keepDirty: false }
    );
  }, [preferences, agencies]);

  const parseAgencyPreference = (rawValue, agencies) => {
    if (!agencies || agencies.length === 0) return [];

    const validAgencyIds = new Set(agencies.map((a) => a.Id));

    if (rawValue === "all") {
      return [...validAgencyIds].sort((a, b) => a - b);
    }

    return String(rawValue || "")
      .split(",")
      .map((x) => parseInt(x.trim(), 10))
      .filter((id) => !isNaN(id) && validAgencyIds.has(id))
      .sort((a, b) => a - b);
  };

  const handleSave = () => {
    const rawValues = getValues();

    const filteredValues = filterUserPreferencesForSave(rawValues, menuVisibility);

    Object.entries(filteredValues).forEach(([key, value]) => {
      setEditableUserPreference(key, value);
    });

    saveUserPreferences(filteredValues, brokerId);
  };

  return (
    <div>
      <Title level={5} style={{ marginTop: 0, marginBottom: 0 }}>
        Preferences
      </Title>
      <Divider style={{ marginTop: 0 }} />

      {isPreferenceGroupVisible("DocumentSearch", menuVisibility) && (
        <Form.Item
          style={{ marginBottom: 8 }}
          label={
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Text strong>Default Selected Agencies for Document Search</Text>
            <AgencyPreferenceTip />
          </span>
          }
        >
          <AgencyMultiSelectEx
            layout={{
              labelCol: { span: 6 },
              wrapperCol: { span: 18 },
            }}
            style={{
              maxWidth: 900
            }}
            control={control}
            agencies={agencies}
            name="DocumentSearchDefaultAgencyKeys"
            isLoading={!agencies || agencies.length === 0}
            setValue={(name, val) => {
              const sorted = Array.isArray(val) ? [...val].sort((a, b) => a - b) : val;
              setValue(name, sorted, { shouldDirty: true });
            }}
            propToWatch={watch("DocumentSearchDefaultAgencyKeys")}
            allowClear
          />
        </Form.Item>
      )}

      {isPreferenceGroupVisible("InventoryThresholds", menuVisibility) && (
        <Form.Item
          label={
            <Text strong>
              Sales Volumes and Thresholds: When plate inventory is below threshold
            </Text>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Controller
              name="DisplayLowInventoryAlertHomePage"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  Display alert on home page
                </Checkbox>
              )}
            />

            <Controller
              name="NotifyLowInventoryByEmail"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  Notify me by email
                </Checkbox>
              )}
            />
          </div>
        </Form.Item>
      )}

      <CustomRadioGroup
        control={control}
        name="UseYesterdayInToDateShortcuts"
        formLabel={
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Text strong>Use Yesterday for Report To-Date Filters</Text>
            <YesterdayShortcutTip />
          </span>
        }
        options={[
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ]}
      />

      {isPreferenceGroupVisible("BatchSorting", menuVisibility) && (
        <Form.Item
          label={
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Text strong>By default, sort documents when batching by:</Text>
            <BatchSortingTip />
          </span>
          }
        >
          <div style={{display: "flex", gap: 16, alignItems: "center"}}>
            <Form.Item style={{ marginBottom: 0 }}>
              <Controller
                name="BatchSortColumn"
                control={control}
                render={({field}) => (
                  <Select
                    {...field}
                    allowClear
                    placeholder="Select column"
                    style={{width: 220}}
                    options={sortColumnOptions}
                  />
                )}
              />
            </Form.Item>

            <CustomRadioGroup
              name="BatchSortAscending"
              control={control}
              formStyle={{ marginBottom: 0 }}
              options={[
                {value: true, label: "Ascending"},
                {value: false, label: "Descending"},
              ]}
            />
          </div>
        </Form.Item>
      )}

      <Form.Item style={{marginTop: 16}}>
        <Button
          size="small"
          type="primary"
          icon={<SaveOutlined/>}
          onClick={handleSave}
          disabled={loading || !isDirty}
        >
        Save Preferences
        </Button>
      </Form.Item>
    </div>
  );
}
